import { FC, useCallback, useMemo, useState } from 'react';

import { Drawer, DrawerHead } from 'views/design-system/components';
import {
	IBundleSession,
	IBundleKeys,
	ITrailEntry,
	IKeyEvidence,
	KeyWeight,
	NameKind,
	useSessionBundle,
} from './use-session-bundle';

import { StepChain } from 'views/design-system/components';
import { toChain, flowName, flowSteps } from './derive';

import './bundle-drawer.scss';

/**
 * MULTI SESSION BUNDLE — the drawer behind the pill on the sessions list.
 *
 * It answers one question: are these the same person, and why should I believe
 * it? Everything on screen is evidence for or against that, ordered by how much
 * it is worth, and the weakest evidence is shown as weak rather than quietly
 * dropped — a reviewer who cannot see what the system leaned on cannot check
 * its work.
 *
 * The drawer decides nothing. Strength, weights and match reasons all arrive
 * from pipelines.api; this file renders them.
 */

/* THE FOUR SLOTS ARE FIXED, AND ALWAYS IN THIS ORDER.
   The spine is meant to be read by SHAPE — a reviewer learns that the first two
   boxes are the ones that matter and stops reading the labels. Sorting them by
   which happened to match, or hiding the empty ones, destroys that: the same
   bundle would draw differently from one row to the next. */
const KEYS: Array<{
	k: 'ssn' | 'id' | 'tel' | 'email';
	short: string;
	label: string;
}> = [
	{ k: 'ssn', short: 'SSN', label: 'SSN (last 4)' },
	{ k: 'id', short: 'ID', label: 'Government ID' },
	{ k: 'tel', short: 'TEL', label: 'Phone' },
	/* `EMAIL`, not `@`. The symbol was the odd one out in a row of words — it
	   asked the reviewer to decode a glyph while the three slots beside it said
	   what they were, and at a glance it read as decoration rather than a
	   labelled slot. Now the row is full width the extra characters cost
	   nothing. */
	{ k: 'email', short: 'EMAIL', label: 'Email' },
];

const STRENGTH_WORD: Record<string, string> = {
	confirmed: 'Confirmed',
	probable: 'Probable',
	possible: 'Possible',
	suggested: 'Suggested',
	conflict: 'Conflict',
};

/** The word a reviewer acts on. Never a score — nobody acts on 0.82. */
const WEIGHT_WORD: Record<KeyWeight, string> = {
	strong: 'Strong',
	weak: 'Weak',
	conflict: 'Conflict',
	saturated: 'Not used',
	none: '—',
};

/* `exact` reads Strong now, not Weak: a first and last name both verified by
   KYC against the document is evidence in its own right. `near` is the
   middle-name-only disagreement. */
const NAME_WORD: Record<NameKind, string> = {
	exact: 'Strong',
	near: 'Middle differs',
	differs: 'Differs',
	none: '—',
};

const fmtDate = (iso: string | null) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

/**
 * The match spine: four fixed slots, then the name tag.
 *
 * EACH SLOT CARRIES ITS WEIGHT, NOT JUST WHETHER IT MATCHED. The slots used to
 * be `on`/`off`, so a matched government ID and a matched phone drew
 * identically — one is proof, the other is a coincidence, and the spine said
 * nothing about the difference. It now emits the same weights the evidence
 * table prints, so the two sections cannot tell a reviewer different things.
 *
 * `conflict` and `saturated` are properties of the KEY across the whole bundle,
 * not of one session, so they win for every card: a key the system declined to
 * count was declined for all of them.
 */
const slotWeight = (
	key: 'ssn' | 'id' | 'tel' | 'email',
	keys: IBundleKeys,
	matched: string[]
): KeyWeight => {
	const w = keys[key]?.weight ?? 'none';
	if (w === 'conflict' || w === 'saturated') return w;
	return matched.includes(key) ? w : 'none';
};

const Spine: FC<{
	matched: string[];
	nameKind: NameKind;
	keys: IBundleKeys;
	small?: boolean;
}> = ({ matched, nameKind, keys, small }) => (
	<>
		<span className={`mspine${small ? ' sm' : ''}`}>
			{KEYS.map(k => {
				const w = slotWeight(k.k, keys, matched);
				return (
					<span
						key={k.k}
						className={`mslot ${w}`}
						title={`${k.label}: ${WEIGHT_WORD[w]}`}
					>
						{k.short}
					</span>
				);
			})}
		</span>
		{/* The name mark NAMES ITSELF, and is still detached from the four slots
		    by a gap so the four-slot scan survives. It DOES go green now: a first
		    and last name both verified by KYC against the document is evidence,
		    not a coincidence — see the note on `.mname` in the scss. */}
		<span
			className={`mname ${nameKind}`}
			title={`Name: ${nameKind === 'none' ? 'no comparison' : nameKind}`}
		>
			name
		</span>
	</>
);

const EvidenceRow: FC<{ label: string; ev: IKeyEvidence; total: number }> = ({
	label,
	ev,
	total,
}) => {
	const dead = ev.weight === 'none';
	const sub =
		ev.note ??
		(ev.shared > 1
			? `shared by ${ev.shared} of ${total} sessions`
			: 'only one session carries this');
	return (
		<div className={`evd${dead ? ' dead' : ''}`}>
			<span className="ek">{label}</span>
			<span className="ev">
				{ev.value || '—'}
				<span className="sub2">{sub}</span>
			</span>
			<span className={`ew ${ev.weight}`}>{WEIGHT_WORD[ev.weight]}</span>
		</div>
	);
};


/**
 * THE HUMAN VERDICT ON ONE CARD.
 *
 * The matcher says these MIGHT be one person. A compliance agent who has
 * actually checked knows something it cannot, so their ruling outranks it —
 * and has to be recorded with a name, a time and, where it overrules the
 * machine, a reason.
 *
 * "Same" is one click: it AGREES with the evidence printed two sections above,
 * and demanding a sentence for each of six cards teaches people to type "ok".
 * "Different" removes the session from the bundle and asserts a permanent
 * negative, so it arms in place and stays armed until a reason exists — no
 * modal, and no timer racing someone mid-sentence.
 */
const CardVerdict: FC<{
	session: IBundleSession;
	/** the anchor's captured name — the operand every question names, so each
	 *  row says what it is being compared against without a scroll back up */
	anchorName: string | null;
	armed: boolean;
	busy: boolean;
	onSame: () => void;
	onArm: () => void;
	onCancel: () => void;
	onCommit: (reason: string) => void;
	onClear: () => void;
}> = ({
	session,
	anchorName,
	armed,
	busy,
	onSame,
	onArm,
	onCancel,
	onCommit,
	onClear,
}) => {
	const [reason, setReason] = useState('');
	const who = anchorName ? (
		<b className="bverdict__name">{anchorName}</b>
	) : (
		'the anchor'
	);

	/* The anchor cannot be a different person from itself. Saying so also
	   documents what every verdict below it means. */
	if (session.anchor) {
		return (
			<div className="bverdict is-anchor">
				<span className="anchor-tag">ANCHOR</span>
				<span className="bverdict__ref">
					Reference session — every card below is ruled against this person
				</span>
			</div>
		);
	}

	/* SETTLED, IN PLACE — the mirror of the `same` state below.
	   This card used to disappear to a shelf the moment it was ruled out, so
	   the reviewer's click looked like it had done nothing. Both verdicts now
	   resolve on the card where they were made. */
	if (session.reviewed === 'different') {
		return (
			<div className="bverdict is-different">
				<span className="bverdict__mark">
					&#10007; Different person &mdash; not {who}
				</span>
				<button className="vbtn ghost" type="button" onClick={onClear} disabled={busy}>
					Change
				</button>
			</div>
		);
	}

	if (session.reviewed === 'same') {
		return (
			<div className="bverdict is-same">
				<span className="bverdict__mark">
					&#10003; Same person &mdash; {who}
				</span>
				<button className="vbtn ghost" type="button" onClick={onClear} disabled={busy}>
					Change
				</button>
			</div>
		);
	}

	if (armed) {
		return (
			<div className="bverdict is-armed">
				<span className="bverdict__q">
					Why is this a different person from {who}?
				</span>
				<textarea
					className="bverdict__reason"
					autoFocus
					value={reason}
					onChange={e => setReason(e.target.value)}
					placeholder="Required — e.g. sibling on the shared household phone; ID numbers differ"
				/>
				<div className="bverdict__row">
					<button
						className="vbtn diff commit"
						type="button"
						disabled={!reason.trim() || busy}
						onClick={() => onCommit(reason.trim())}
					>
						Confirm &mdash; different person
					</button>
					<button className="vbtn ghost" type="button" onClick={onCancel}>
						Cancel
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="bverdict is-open">
			<span className="bverdict__q">Same person as {who}?</span>
			<button className="vbtn same" type="button" onClick={onSame} disabled={busy}>
				Same person
			</button>
			<button className="vbtn diff" type="button" onClick={onArm} disabled={busy}>
				Different person
			</button>
		</div>
	);
};

/** One entry in the chronology. Verdicts are tinted so the decisions stand out
 *  from the prose around them — the trail is mostly notes, the rulings are its
 *  spine. */
const TrailEntry: FC<{ entry: ITrailEntry }> = ({ entry }) => {
	const word =
		entry.verdict === 'same'
			? 'marked this session as the same person'
			: entry.verdict === 'different'
			? 'marked this session as a different person'
			: 'cleared their verdict on this session';
	return (
		<div className={`bnote${entry.type === 'verdict' ? ' is-verdict' : ''}`}>
			<div className="bnote__head">
				<b>{entry.authorName}</b>
				<span className="bnote__at">{fmtDate(entry.createdAt)}</span>
			</div>
			<div className="bnote__body">
				{entry.type === 'verdict' ? (
					<>
						<span className={`bnote__act ${entry.verdict}`}>{word}</span>
						{entry.note ? (
							<span className="bnote__txt"> &mdash; &ldquo;{entry.note}&rdquo;</span>
						) : null}
					</>
				) : (
					<span className="bnote__txt">{entry.note}</span>
				)}
			</div>
		</div>
	);
};

export const BundleDrawer: FC<{
	sessionId: string | null;
	open: boolean;
	onClose: () => void;
	/* The whole session, not an id. Callers cannot look these up: five of the
	   six sessions in a typical bundle are NOT on the current list page. */
	onOpenSession: (session: IBundleSession) => void;
	/** fired after any successful write, so the list behind can re-read its
	 *  bundle counts — a verdict changes how many sessions the bundle has */
	onChanged?: () => void;
}> = ({ sessionId, open, onClose, onOpenSession, onChanged }) => {
	const { bundle, loading, error, post, posting } = useSessionBundle(
		sessionId,
		open
	);
	const [armedId, setArmedId] = useState<string | null>(null);
	const [postError, setPostError] = useState<string | null>(null);
	/* Every write goes through here so nothing can forget to tell the list. */
	const write = useCallback(
		async (entry: Parameters<typeof post>[0]) => {
			setPostError(null);
			const r = await post(entry);
			if (r?.ok) onChanged?.();
			else setPostError(r?.message ?? null);
			return r;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[post, onChanged]
	);
	const [noteText, setNoteText] = useState('');
	const [term, setTerm] = useState('');
	const [sort, setSort] = useState<'updated' | 'created' | 'status' | 'fund'>(
		'updated'
	);

	const rows = useMemo(() => {
		if (!bundle) return [] as IBundleSession[];
		const q = term.trim().toLowerCase();
		const filtered = q
			? bundle.sessions.filter(s =>
					[s.fund, s.name, s.email, s.status, s._id]
						.filter(Boolean)
						.join(' ')
						.toLowerCase()
						.includes(q)
			  )
			: bundle.sessions;
		const sorted = [...filtered].sort((a, b) => {
			/* The anchor is the row the reviewer came FROM. It stays pinned to the
			   top under every sort — losing it in a re-sorted list means losing
			   your place in the queue. */
			if (a.anchor) return -1;
			if (b.anchor) return 1;
			if (sort === 'fund') return (a.fund ?? '') < (b.fund ?? '') ? -1 : 1;
			if (sort === 'status')
				return (a.status ?? '') < (b.status ?? '') ? -1 : 1;
			const key = sort === 'created' ? 'createdAt' : 'updatedAt';
			return new Date(b[key] ?? 0).getTime() - new Date(a[key] ?? 0).getTime();
		});
		return sorted;
	}, [bundle, term, sort]);

	const anchorName = bundle?.sessions.find(s => s.anchor)?.name || null;
	const title = anchorName || 'Bundle';

	return (
		<Drawer open={open} label="Multi session bundle">
			<DrawerHead
				title={title}
				onClose={onClose}
				chip={
					bundle ? (
						<span className={`mch ${bundle.strength}`}>
							{STRENGTH_WORD[bundle.strength]}
						</span>
					) : undefined
				}
			>
				{bundle && (
					<div className="idline">
						{bundle.sessions.find(s => s.anchor)?.email}
						{bundle.keys.id.value ? ` · ID ${bundle.keys.id.value}` : ''}
					</div>
				)}
			</DrawerHead>

			{loading && <div className="bdl__state">Loading bundle…</div>}
			{error && !loading && <div className="bdl__state bdl__state--bad">{error}</div>}

			{bundle && !loading && (
				<>
					<div className="dsec">
						<div className="sh">
							Why these are one person
							<span className="r2">{bundle.total + 1} sessions</span>
						</div>
						<div className="evbox">
							{KEYS.map(k => (
								<EvidenceRow
									key={k.k}
									label={k.label}
									ev={bundle.keys[k.k]}
									total={bundle.total + 1}
								/>
							))}
							{/* NAME IS LAST BECAUSE IT IS WORTH LEAST. */}
							<div className={`evd${bundle.name.kind === 'none' ? ' dead' : ''}`}>
								<span className="ek">Name</span>
								<span className="ev">
									{bundle.name.value || '—'}
									<span className="sub2">
										{bundle.name.why ??
											(bundle.name.kind === 'exact'
												? 'identical after normalising case, accents and punctuation'
												: 'not captured')}
									</span>
								</span>
								<span className={`ew ${bundle.name.kind}`}>
									{NAME_WORD[bundle.name.kind]}
								</span>
							</div>
							{bundle.name.kind === 'differs' && (
								<span className="enote">
									This argues AGAINST the bundle. The identifiers above decide it
									— names are re-spelled, changed and shared.
								</span>
							)}
						</div>
					</div>

					<div className="dsec">
						<div className="sh">
							Sessions in this bundle
							<span className="r2">
								{rows.length} of {bundle.total + 1} shown
							</span>
						</div>

						{/* Decode the marks where the eye meets them. The swatches are
						    real `.mslot` chips, so this cannot drift from the spine. */}
						<div className="mlegend">
							<span>
								<span className="mslot strong">ID</span>strong — one person, one
								number
							</span>
							<span>
								<span className="mslot weak">TEL</span>weak — shared, recycled
							</span>
							<span>
								<span className="mslot conflict">ID</span>conflict — values differ
							</span>
							<span>
								<span className="mslot saturated">EMAIL</span>not used — too common to
								point at anyone
							</span>
							<span>
								<span className="mslot none">SSN</span>no match
							</span>
							<span>
								<span className="mname exact">name</span>first + last agree
							</span>
						</div>

						<div className="bctl">
							<label className="srch">
								<input
									value={term}
									onChange={e => setTerm(e.target.value)}
									placeholder="Search fund, status, session id"
								/>
							</label>
							<select
								className="bsel"
								value={sort}
								onChange={e => setSort(e.target.value as any)}
							>
								<option value="updated">Sort: Last updated</option>
								<option value="created">Sort: Created</option>
								<option value="status">Sort: Status</option>
								<option value="fund">Sort: Fund A–Z</option>
							</select>
						</div>

						{/* A BUNDLE HELD TOGETHER BY NOTHING STRONG SAYS SO. */}
						{bundle.strength === 'possible' ||
						bundle.strength === 'suggested' ? (
							<div className="bwarn">
								<b>Weak evidence only.</b> Nothing here is a government-issued
								identifier — a shared phone or household email is routinely used
								by more than one person. Treat this as a lead: confirm before you
								act on it.
							</div>
						) : null}

						{bundle.capped && (
							<div className="bwarn bwarn--info">
								Showing the {bundle.sessions.length - 1} most recently updated of{' '}
								{bundle.total} matched sessions.
							</div>
						)}

						<div className="blist">
							{rows.map(s => (
								<div
									key={s._id}
									className={`bcard${s.anchor ? ' anchor' : ''}${
										s.reviewed === 'same' ? ' ruled-same' : ''
									}${s.reviewed === 'different' ? ' ruled-out' : ''}`}
								>
									<CardVerdict
										anchorName={anchorName}
										session={s}
										armed={armedId === s._id}
										busy={posting}
										onSame={async () => {
											await write({
												type: 'verdict',
												targetSessionId: s._id,
												verdict: 'same',
											});
										}}
										onArm={() => setArmedId(s._id)}
										onCancel={() => setArmedId(null)}
										onCommit={async reason => {
											const r = await write({
												type: 'verdict',
												targetSessionId: s._id,
												verdict: 'different',
												note: reason,
											});
											if (r?.ok) setArmedId(null);
										}}
										onClear={async () => {
											await write({
												type: 'verdict',
												targetSessionId: s._id,
												verdict: 'cleared',
											});
										}}
									/>
									<div className="bcard__top">
										<span className="bcard__fund">{s.fund || 'No fund'}</span>
										<span className={`vch ${s.status ?? ''}`}>
											{(s.status ?? 'unknown').toUpperCase()}
										</span>
									</div>
									<div className="bcard__id mono">{s._id}</div>
									<div className="bcard__who">
										<b>{s.name || '—'}</b>
										{s.email ? <span className="mail"> · {s.email}</span> : null}
									</div>
									<div className="bcard__meta mono">
										Created {fmtDate(s.createdAt)} · Updated{' '}
										{fmtDate(s.updatedAt)}
									</div>
									{/* THE JOURNEY, NOT JUST THE IDENTITY. Deciding whether two
									    sessions are one person means seeing what each one actually
									    did — one stopped at KYC, the other signed. Same `toChain`
									    the list rows use, so the pills cannot disagree with the
									    queue behind the drawer. */}
									{s.nodes?.length ? (
										<div className="bcard__chain">
											<StepChain steps={toChain(s)} />
										</div>
									) : null}
									{s.onBoardingName ? (
										<div className="bcard__flow">
											{flowName(s)}
											{flowSteps(s) ? ` — ${flowSteps(s)}` : ''}
										</div>
									) : null}
									<div className="bcard__match">
										<Spine
											matched={s.matched}
											nameKind={s.nameKind}
											keys={bundle.keys}
											small
										/>
									</div>
									{!s.anchor && (
										<div className="bcard__why">
											{s.matched.length
												? `Matched on ${s.matched
														.map(m => KEYS.find(k => k.k === m)?.label ?? m)
														.join(' + ')}`
												: 'Linked by name only'}
										</div>
									)}
									<div className="bcard__acts">
										<button
											className="dbtn pri"
											type="button"
											onClick={() => onOpenSession(s)}
										>
											Open session →
										</button>
									</div>
								</div>
							))}
							{!rows.length && (
								<div className="bdl__state">No session matches that search.</div>
							)}
						</div>
					</div>


					{/* THE RECORD. Append-only, attributed, timestamped — a verdict, its
					    reversal and its restoration each write their own entry, so an
					    auditor can reconstruct what the system claimed, what a person
					    concluded, when, and why. */}
					<div className="dsec">
						<div className="sh">
							Review trail
							<span className="r2">
								{bundle.trail?.length || 'no'} entr
								{bundle.trail?.length === 1 ? 'y' : 'ies'}
							</span>
						</div>

						{!bundle.trail?.length && (
							<div className="bdl__state">
								No human has ruled on this bundle yet. Verdicts and notes land
								here, timestamped and attributed, and are never deleted.
							</div>
						)}

						<div className="btrail">
							{(bundle.trail ?? []).map(t => (
								<TrailEntry key={t._id} entry={t} />
							))}
						</div>

						{postError && (
							<div className="bdl__state bdl__state--bad">{postError}</div>
						)}

						{/* Hidden when no key is strong enough to name this bundle —
						    writing entries nothing can read back later is worse than
						    offering no composer. */}
						{bundle.identity && (
							<div className="bcompose">
								<textarea
									value={noteText}
									onChange={e => setNoteText(e.target.value)}
									placeholder="Add a note to this bundle’s record…"
								/>
								<div className="bcompose__row">
									<button
										className="dbtn pri"
										type="button"
										disabled={posting || !noteText.trim()}
										onClick={async () => {
											const r = await write({
												type: 'note',
												note: noteText.trim(),
											});
											if (r?.ok) setNoteText('');
										}}
									>
										{posting ? 'Posting…' : 'Add note'}
									</button>
								</div>
							</div>
						)}
					</div>

					<div className="dsec dsec--last">
						<div className="sh">Reading this drawer</div>
						<div className="dhelp">
							A <b>government ID</b> number is strong evidence — issued once,
							to one person. The <b>SSN</b> is only its last four digits: ten
							thousand possible values, so it corroborates but cannot carry a
							bundle on its own. <b>Phone</b> and <b>email</b> are weak too:
							families share them, assistants file on behalf of others, carriers
							recycle numbers. A key carried by dozens of sessions is marked{' '}
							<b>Not used</b> — it identifies nobody, so it was not used to build
							this bundle. The <b>name</b> tag reads the given name and surname
							KYC verified against the document: both agreeing is strong,
							agreeing except for a middle name is a question rather than a
							mismatch, and a differing surname is the thing to stop on — two
							people behind one ID number.
						</div>
					</div>
				</>
			)}
		</Drawer>
	);
};
